/* BulkSeoPanel — admin panel to draft + apply SEO overrides across many
 * recipes and historical recipes at once. Uses the per-item suggest-seo
 * endpoints already in the API (one Claude call per row) so the backend
 * doesn't hit the Cloudflare 100s edge timeout on big batches. The client
 * fans out sequentially with a small concurrency cap.
 *
 * Flow:
 *   1. GET /admin/seo/missing → list of recipes/historical missing at
 *      least one SEO field.
 *   2. Admin clicks "Draft all" (or per-row) → we POST each item to its
 *      suggest-seo endpoint; result populates the editable inputs.
 *   3. Admin can tweak individual rows.
 *   4. "Apply drafted" → PATCH each drafted row.
 *
 * Admin-only via the same _require_admin gate as the underlying endpoints.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, ShieldCheck, Check, ArrowClockwise } from "@phosphor-icons/react";

const CONCURRENCY = 2;               // parallel Claude calls; keep polite
const MAX_TITLE = 110;
const MAX_DESC = 200;
const MAX_ALT = 150;

export default function BulkSeoPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");        // all | recipes | historical | ... | import-pages
  const [busyIds, setBusyIds] = useState(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });
  const [importPages, setImportPages] = useState(null);   // { total, pages: [...] }
  // Kitchen-only refine controls: search any term (even ones with complete
  // SEO) so admins can "call up" a specific entry and tighten its meta later.
  const [kitchenQuery, setKitchenQuery] = useState("");
  const [includeComplete, setIncludeComplete] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // "import-pages" mode = read-only landing-page audit. Everything else
      // goes through the standard missing-fields listing endpoint.
      if (kind === "import-pages") {
        const { data } = await api.get("/admin/seo/import-pages");
        setImportPages(data);
        setItems([]);
      } else {
        const params = new URLSearchParams({ kind });
        if (kind === "kitchen") {
          if (kitchenQuery.trim()) params.set("q", kitchenQuery.trim());
          if (includeComplete) params.set("include_complete", "true");
        }
        const { data } = await api.get(`/admin/seo/missing?${params.toString()}`);
        /* Local editable copy per row. `dirty` = user or AI has staged
         * changes; `applied` = successfully written to DB in this session. */
        setItems((data.items || []).map((r) => ({ ...r, dirty: false, applied: false })));
        setImportPages(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load SEO data");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [kind]);       // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = (idx, patch) => {
    setItems((prev) => prev.map((r, i) => i === idx ? { ...r, ...patch, dirty: true, applied: false } : r));
  };

  const suggestOne = async (row) => {
    const endpoint =
      row.type === "historical" ? `/admin/historical/recipes/${row.id}/suggest-seo`
      : row.type === "blog"     ? `/admin/blog/${row.id}/suggest-seo`
      : row.type === "cookbook" ? `/cookbooks/${row.id}/suggest-seo`
      : row.type === "kitchen"  ? `/admin/kitchen-help/${row.id}/suggest-seo`
      :                           `/admin/recipes/${row.id}/suggest-seo`;
    const { data } = await api.post(endpoint);
    return data || {};
  };

  const applyOne = async (row) => {
    const endpoint =
      row.type === "historical" ? `/admin/historical/recipes/${row.id}`
      : row.type === "blog"     ? `/admin/blog/${row.id}`
      : row.type === "cookbook" ? `/cookbooks/${row.id}`
      : row.type === "kitchen"  ? `/admin/kitchen-help/${row.id}`
      :                           `/admin/recipes/${row.id}`;
    await api.patch(endpoint, {
      seo_title: row.seo_title || "",
      seo_description: row.seo_description || "",
      seo_keywords: row.seo_keywords || "",
      image_alt: row.image_alt || "",
    });
  };

  const draftRow = async (idx) => {
    const row = items[idx];
    if (!row) return;
    setBusyIds((prev) => new Set(prev).add(row.id));
    try {
      const d = await suggestOne(row);
      if (!d.seo_title && !d.seo_description && !d.image_alt && !d.seo_keywords) {
        toast.error(`No AI suggestion for "${row.title}"`);
        return;
      }
      updateRow(idx, {
        seo_title: d.seo_title || row.seo_title,
        seo_description: d.seo_description || row.seo_description,
        seo_keywords: d.seo_keywords || row.seo_keywords,
        image_alt: d.image_alt || row.image_alt,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || `AI draft failed for "${row.title}"`);
    } finally {
      setBusyIds((prev) => { const n = new Set(prev); n.delete(row.id); return n; });
    }
  };

  const applyRow = async (idx) => {
    const row = items[idx];
    if (!row) return;
    setBusyIds((prev) => new Set(prev).add(row.id));
    try {
      await applyOne(row);
      setItems((prev) => prev.map((r, i) => i === idx ? { ...r, applied: true, dirty: false } : r));
      toast.success(`Applied to "${row.title}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setBusyIds((prev) => { const n = new Set(prev); n.delete(row.id); return n; });
    }
  };

  /* Draft every un-drafted row with a small concurrency cap. Rows that
   * already have staged edits are skipped so a partial-hand-edit session
   * isn't overwritten. */
  const draftAll = async () => {
    const targets = items
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => !r.dirty && !r.applied);
    if (targets.length === 0) { toast.info("Nothing to draft"); return; }
    setBatchBusy(true);
    setBatchProgress({ done: 0, total: targets.length });
    let done = 0;
    const workers = Array.from({ length: CONCURRENCY }, async () => {
      while (targets.length) {
        const next = targets.shift();
        if (!next) break;
        try { await draftRow(next.i); } catch { /* individual toast already fired */ }
        done += 1;
        setBatchProgress({ done, total: batchProgress.total || done });
      }
    });
    await Promise.all(workers);
    setBatchBusy(false);
    toast.success(`Drafted ${done} item${done === 1 ? "" : "s"}`);
  };

  const applyAllDrafted = async () => {
    const targets = items.map((r, i) => ({ r, i })).filter(({ r }) => r.dirty);
    if (targets.length === 0) { toast.info("Nothing to apply — draft first"); return; }
    setBatchBusy(true);
    setBatchProgress({ done: 0, total: targets.length });
    let done = 0;
    for (const { i } of targets) {
      try { await applyRow(i); } catch { /* per-row toast */ }
      done += 1;
      setBatchProgress({ done, total: targets.length });
    }
    setBatchBusy(false);
    toast.success(`Applied ${done} item${done === 1 ? "" : "s"}`);
  };

  const draftedCount = items.filter((r) => r.dirty).length;

  return (
    <div data-testid="admin-bulk-seo-panel">
      <div className="bg-white rounded-3xl p-6 border border-rf-ink/5 mb-6">
        <p className="overline mb-1">Bulk SEO</p>
        <h2 className="font-display font-black text-2xl text-rf-ink mb-2">Draft &amp; apply search engine metadata across every indexable page</h2>
        <p className="text-sm text-rf-ink-soft mb-4">
          Every row below is a public recipe, historical recipe, news post, cookbook, or Kitchen Help term that&apos;s missing at least one of <code>seo_title</code>, <code>seo_description</code>, or <code>image_alt</code>. Click <b>Draft all</b> to run Claude across the whole list, review the suggestions in place, then <b>Apply drafted</b> to write them to the database. You can also draft / apply row-by-row.
        </p>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {[
            { k: "all", label: "All" },
            { k: "recipes", label: "Recipes only" },
            { k: "historical", label: "Historical only" },
            { k: "blog", label: "News only" },
            { k: "cookbooks", label: "Cookbooks only" },
            { k: "kitchen", label: "Kitchen Help only" },
            { k: "import-pages", label: "Import pages (audit)" },
          ].map((f) => (
            <button
              key={f.k}
              type="button"
              data-testid={`admin-bulk-seo-filter-${f.k}`}
              onClick={() => setKind(f.k)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold ${kind === f.k ? "bg-rf-ink text-rf-cream" : "bg-rf-cream text-rf-ink-soft border border-rf-ink/10"}`}
            >{f.label}</button>
          ))}
          <span className="text-xs text-rf-ink-soft ml-1">
            {loading ? "Loading…" : `${items.length} item${items.length === 1 ? "" : "s"} need SEO`}
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={load}
            disabled={loading || batchBusy}
            data-testid="admin-bulk-seo-refresh"
            className="btn-outline-ink !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
          >
            <ArrowClockwise size={12} weight="bold" /> Refresh
          </button>
          <button
            type="button"
            onClick={draftAll}
            disabled={loading || batchBusy || items.length === 0}
            data-testid="admin-bulk-seo-draft-all"
            className="btn-outline-ink !py-1.5 !px-3 text-xs inline-flex items-center gap-1 border-rf-orange/50 !text-rf-orange"
          >
            <Sparkle size={12} weight="fill" /> Draft all missing
          </button>
          <button
            type="button"
            onClick={applyAllDrafted}
            disabled={loading || batchBusy || draftedCount === 0}
            data-testid="admin-bulk-seo-apply-all"
            className="btn-amber !py-1.5 !px-3 text-xs inline-flex items-center gap-1 disabled:opacity-50"
          >
            <ShieldCheck size={12} weight="bold" /> Apply drafted ({draftedCount})
          </button>
        </div>

        {batchBusy && (
          <div className="w-full bg-rf-cream rounded-full h-2 mb-4 overflow-hidden">
            <div
              className="bg-rf-orange h-full transition-all"
              style={{ width: `${batchProgress.total ? Math.round((batchProgress.done / batchProgress.total) * 100) : 0}%` }}
              data-testid="admin-bulk-seo-progress"
            />
          </div>
        )}

        {kind === "kitchen" && (
          <div className="mt-1 flex flex-wrap items-center gap-3 bg-rf-cream/40 border border-rf-orange/20 rounded-2xl px-4 py-3">
            <div className="flex-1 min-w-[240px]">
              <input
                type="text"
                value={kitchenQuery}
                onChange={(e) => setKitchenQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                placeholder="Search a specific term (e.g. julienne, blanch, muslin) to refine…"
                data-testid="admin-bulk-seo-kitchen-search"
                className="input-rf w-full !text-sm !py-2"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-bold text-rf-ink-soft" data-testid="admin-bulk-seo-include-complete-label">
              <input
                type="checkbox"
                checked={includeComplete}
                onChange={(e) => setIncludeComplete(e.target.checked)}
                data-testid="admin-bulk-seo-include-complete"
              />
              Include entries already completed
            </label>
            <button
              type="button"
              onClick={load}
              disabled={loading || batchBusy}
              data-testid="admin-bulk-seo-kitchen-search-btn"
              className="btn-amber !py-1.5 !px-4 text-xs"
            >
              Search
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-rf-ink-soft">Loading…</div>
      ) : kind === "import-pages" ? (
        <ImportPagesAudit data={importPages} />
      ) : items.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center text-rf-ink-soft border border-rf-ink/5">
          🎉 Every public recipe, historical, news post & cookbook has full SEO metadata. Nice.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r, idx) => {
            const busy = busyIds.has(r.id);
            const targetUrl =
              r.type === "historical" ? `/historical/${r.slug || r.id}`
              : r.type === "blog"     ? `/news/${r.slug || r.id}`
              : r.type === "cookbook" ? `/cb/${r.slug || r.id}`
              : r.type === "kitchen"  ? `/kitchen-help/${r.slug || r.id}`
              :                         `/r/${r.slug || r.id}`;
            const badgeClass =
              r.type === "historical" ? "bg-amber-900 text-rf-cream"
              : r.type === "blog"     ? "bg-blue-700 text-white"
              : r.type === "cookbook" ? "bg-emerald-700 text-white"
              : r.type === "kitchen"  ? "bg-pink-600 text-white"
              :                         "bg-rf-orange text-white";
            return (
              <div
                key={`${r.type}-${r.id}`}
                data-testid={`admin-bulk-seo-row-${r.id}`}
                className={`bg-white rounded-2xl border p-4 transition-colors ${r.applied ? "border-emerald-400/60" : r.dirty ? "border-rf-orange/50" : "border-rf-ink/5"}`}
              >
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-shrink-0 md:w-24">
                    <div className="aspect-[4/3] bg-gradient-to-br from-amber-800 to-rf-ink rounded-lg overflow-hidden relative">
                      {r.image_url ? (
                        <img
                          src={r.image_url}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : null}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2 mb-1">
                      <span className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded ${badgeClass}`}>
                        {r.type}
                      </span>
                      <a
                        href={targetUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="font-display font-black text-base text-rf-ink hover:text-rf-orange truncate"
                      >
                        {r.title || "(untitled)"}
                      </a>
                      <span className="text-xs text-rf-ink-soft truncate">{r.subtitle}</span>
                      {r.applied && (
                        <span className="text-[10px] font-black text-emerald-700 inline-flex items-center gap-1">
                          <Check size={10} weight="bold" /> APPLIED
                        </span>
                      )}
                    </div>

                    <div className="grid md:grid-cols-3 gap-2 mt-2">
                      <div>
                        <label className="block text-[10px] uppercase tracking-widest font-black text-rf-ink-soft mb-1">SEO title</label>
                        <input
                          type="text"
                          className="input-rf w-full !text-xs !py-1.5"
                          value={r.seo_title || ""}
                          maxLength={MAX_TITLE}
                          onChange={(e) => updateRow(idx, { seo_title: e.target.value })}
                          placeholder={r.title}
                          data-testid={`admin-bulk-seo-title-${r.id}`}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-widest font-black text-rf-ink-soft mb-1">Description</label>
                        <textarea
                          rows={2}
                          className="input-rf w-full !text-xs !py-1.5"
                          value={r.seo_description || ""}
                          maxLength={MAX_DESC}
                          onChange={(e) => updateRow(idx, { seo_description: e.target.value })}
                          placeholder="Search result snippet…"
                          data-testid={`admin-bulk-seo-desc-${r.id}`}
                        />
                        <p className="text-[9px] text-rf-ink-soft">{(r.seo_description || "").length} / {MAX_DESC}</p>
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-widest font-black text-rf-ink-soft mb-1">Image alt</label>
                        <input
                          type="text"
                          className="input-rf w-full !text-xs !py-1.5"
                          value={r.image_alt || ""}
                          maxLength={MAX_ALT}
                          onChange={(e) => updateRow(idx, { image_alt: e.target.value })}
                          placeholder="Alt text…"
                          data-testid={`admin-bulk-seo-alt-${r.id}`}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase tracking-widest font-black text-rf-ink-soft mb-1">Keywords (comma-sep)</label>
                        <textarea
                          rows={2}
                          className="input-rf w-full !text-xs !py-1.5"
                          value={r.seo_keywords || ""}
                          maxLength={400}
                          onChange={(e) => updateRow(idx, { seo_keywords: e.target.value })}
                          placeholder="chicken curry recipe, easy weeknight supper, one-pot chicken…"
                          data-testid={`admin-bulk-seo-keywords-${r.id}`}
                        />
                        <p className="text-[9px] text-rf-ink-soft">{(r.seo_keywords || "").length} / 400</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => draftRow(idx)}
                        disabled={busy || batchBusy}
                        data-testid={`admin-bulk-seo-draft-${r.id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-rf-orange hover:underline disabled:opacity-40"
                      >
                        <Sparkle size={11} weight="fill" /> {busy ? "Drafting…" : "Draft with AI"}
                      </button>
                      <button
                        type="button"
                        onClick={() => applyRow(idx)}
                        disabled={busy || batchBusy || (!r.dirty && !r.applied)}
                        data-testid={`admin-bulk-seo-apply-${r.id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-rf-ink hover:underline disabled:opacity-40"
                      >
                        <ShieldCheck size={11} weight="bold" /> Apply
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



/* Read-only audit tab for the /import + /try + /import-from-* landing
 * pages. Because these are static React pages (not DB rows) they can't have
 * "missing" fields the way user-generated content can — this view shows
 * that every page has curated title, keywords, and the expected JSON-LD
 * schemas so admins get the same at-a-glance visibility. Click any row to
 * preview the live page in a new tab. */
function ImportPagesAudit({ data }) {
  if (!data) return null;
  const KIND_STYLES = {
    app:     "bg-rf-orange/15 text-rf-orange",
    notes:   "bg-blue-100 text-blue-700",
    web:     "bg-emerald-100 text-emerald-700",
    legacy:  "bg-amber-200/60 text-amber-900",
    rescue:  "bg-pink-100 text-pink-700",
    hub:     "bg-rf-ink text-rf-cream",
  };
  return (
    <div className="bg-white rounded-3xl p-6 border border-rf-ink/5" data-testid="admin-import-pages-audit">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <SummaryStat label="Pages indexed" value={data.total} />
        <SummaryStat label="With curated keywords" value={data.with_keywords} />
        <SummaryStat label="With JSON-LD schemas" value={data.with_schemas} />
        <SummaryStat label="In sitemap.xml" value={data.pages.filter((p) => p.in_sitemap).length} />
      </div>
      <p className="text-xs text-rf-ink-soft mb-4">
        Landing-page copy is curated in <code>ImportLanding.jsx</code>. Change or add a source there → the audit auto-updates on next deploy.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-rf-ink-soft border-b border-rf-ink/10">
            <tr>
              <th className="py-2 pr-4">Page</th>
              <th className="py-2 pr-4">Kind</th>
              <th className="py-2 pr-4">Title</th>
              <th className="py-2 pr-4">Keywords</th>
              <th className="py-2 pr-4">Schemas</th>
              <th className="py-2">Preview</th>
            </tr>
          </thead>
          <tbody>
            {data.pages.map((p) => (
              <tr key={p.slug} className="border-b border-rf-ink/5 hover:bg-rf-cream/40" data-testid={`admin-import-page-row-${p.slug}`}>
                <td className="py-3 pr-4 font-semibold text-rf-ink">
                  <div>{p.label}</div>
                  <div className="text-[11px] text-rf-ink-soft font-normal">/{p.slug}</div>
                </td>
                <td className="py-3 pr-4">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide ${KIND_STYLES[p.kind] || "bg-slate-100 text-slate-700"}`}>
                    {p.kind}
                  </span>
                </td>
                <td className="py-3 pr-4 text-rf-ink-soft max-w-xs truncate" title={p.title}>{p.title}</td>
                <td className="py-3 pr-4 max-w-md">
                  {p.has_keywords ? (
                    <span className="text-emerald-700 text-xs" title={p.keywords}>
                      ✓ {p.keywords.split(",").slice(0, 3).join(",")}
                      {p.keywords.split(",").length > 3 && ` +${p.keywords.split(",").length - 3}`}
                    </span>
                  ) : (
                    <span className="text-rf-ink-soft text-xs italic">Hub page (no keywords)</span>
                  )}
                </td>
                <td className="py-3 pr-4">
                  <div className="flex flex-wrap gap-1">
                    {p.schemas.map((s) => (
                      <span key={s} className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold">{s}</span>
                    ))}
                  </div>
                </td>
                <td className="py-3">
                  <a href={p.url} target="_blank" rel="noreferrer"
                     className="text-rf-orange text-xs font-semibold underline">
                    Open →
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SummaryStat({ label, value }) {
  return (
    <div className="bg-rf-cream/60 rounded-xl p-3">
      <div className="text-2xl font-black text-rf-ink font-display">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-rf-ink-soft">{label}</div>
    </div>
  );
}
